import { FunctionComponent } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'src/utils/twMerge';

import CdnIcon from 'src/designSystem/CdnIcon';
import {
  STREAK_DAY_LOOKS,
  STREAK_STRIP_UPCOMING,
} from 'src/components/Streak/page/utils/streakDayLook';
import {
  freezeDatesOf,
  useStreakDays,
  useStreakSummary,
} from 'src/queries/streak';
import { useStreakCelebrationTiers } from 'src/queries/streakGoal';
import { countedDatesOf, streakDayState } from 'src/utils/streak';
import { toDayKey } from 'src/utils/date';

const shortDayName = (day: Dayjs, locale: string): string =>
  day.locale(locale).format('ddd').replace(/\.$/, '');

const daysOfWeek = (reference: Dayjs): Dayjs[] => {
  const start = reference.startOf('week');
  return Array.from({ length: 7 }, (_, index) => start.add(index, 'day'));
};

type Props = {
  today?: Dayjs;
  className?: string;
};

const StreakWeekStrip: FunctionComponent<Props> = ({
  today = dayjs(),
  className,
}) => {
  const { i18n } = useTranslation();
  const days = daysOfWeek(today);

  const { data: week } = useStreakDays(
    toDayKey(days[0]),
    toDayKey(days[6]),
  );
  const { data: summary } = useStreakSummary();
  const dates = countedDatesOf(week, summary);
  const freezeDates = freezeDatesOf(week);
  const celebrationTiers = useStreakCelebrationTiers();

  return (
    <div
      data-testid="streak-week-strip"
      className={twMerge(
        'flex w-full flex-col gap-2 rounded-2xl bg-structural-lightest p-5',
        className,
      )}
    >
      <div className="flex w-full items-start justify-between">
        {days.map(day => (
          <div
            key={toDayKey(day)}
            className="flex h-[18px] w-9 items-center justify-center text-xs font-semibold text-structural-neutral"
          >
            <span className="capitalize">
              {shortDayName(day, i18n.language)}
            </span>
          </div>
        ))}
      </div>
      <div className="flex w-full items-start justify-between">
        {days.map(day => {
          const state = week
            ? streakDayState(day, dates, today, freezeDates, celebrationTiers)
            : 'upcoming';
          const look = STREAK_DAY_LOOKS[state] ?? STREAK_STRIP_UPCOMING;
          return (
            <div
              key={toDayKey(day)}
              data-testid={`strip-day-${state}`}
              className={twMerge(
                'flex size-9 items-center justify-center rounded-full',
                look.disc,
              )}
            >
              {look.icon ? (
                <CdnIcon name={look.icon} className="h-5 w-auto" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StreakWeekStrip;
